import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Tag } from "./ui";

/**
 * Busca rápida global (Ctrl+K, ou ⌘K no Mac). Procura clientes, contratos e
 * processos e navega direto para a página com o filtro já preenchido (?q=).
 * Também aceita "ir para" as seções pelo nome. Navegação por teclado:
 * ↑ ↓ movem, Enter abre, Esc fecha.
 */

const SECOES_ATALHO = [
    { rota: "/inicio", rotulo: "Início" },
    { rota: "/agenda", rotulo: "Agenda" },
    { rota: "/contratos", rotulo: "Contratos" },
    { rota: "/processos", rotulo: "Processos" },
    { rota: "/prazos", rotulo: "Prazos" },
    { rota: "/pagamentos", rotulo: "Pagamentos" },
    { rota: "/clientes", rotulo: "Clientes" }
];

const BuscaRapida = ({ aberto, onFechar }) => {
    const navigate = useNavigate();
    const [termo, setTermo] = useState("");
    const [resultados, setResultados] = useState([]);
    const [ativo, setAtivo] = useState(0);
    const inputRef = useRef(null);

    // Foca o campo ao abrir; limpa ao fechar
    useEffect(() => {
        if (aberto) {
            setTermo("");
            setResultados([]);
            setAtivo(0);
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [aberto]);

    // Busca com debounce leve
    useEffect(() => {
        if (!aberto) return;
        const t = termo.trim();

        if (t.length < 2) {
            // Só o texto curto: oferece as seções que casam com o que foi digitado
            const secoes = SECOES_ATALHO
                .filter((s) => !t || s.rotulo.toLowerCase().includes(t.toLowerCase()))
                .map((s) => ({ tipo: "secao", titulo: s.rotulo, sub: "Ir para a seção", rota: s.rota }));
            setResultados(secoes);
            setAtivo(0);
            return;
        }

        let cancelado = false;
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get(`/busca?q=${encodeURIComponent(t)}`);
                if (cancelado) return;

                const lista = [
                    ...data.clientes.map((c) => ({
                        tipo: "cliente", titulo: c.nome, sub: `CPF ${c.CPF} · ${c.cidade}/${c.UF}`,
                        rota: `/clientes?q=${encodeURIComponent(c.nome)}`
                    })),
                    ...data.contratos.map((c) => ({
                        tipo: "contrato", titulo: `Contrato ${c.cod_contrato} — ${c.nome_cliente}`,
                        sub: `${c.tipo_servico} · ${c.status_contrato}`,
                        rota: `/contratos?q=${encodeURIComponent(c.nome_cliente)}`
                    })),
                    ...data.processos.map((p) => ({
                        tipo: "processo", titulo: p.num_processo, sub: `${p.nome_cliente} · ${p.status_processo}`,
                        rota: `/processos?q=${encodeURIComponent(p.num_processo)}`
                    }))
                ];
                setResultados(lista);
                setAtivo(0);
            } catch {
                /* silencioso */
            }
        }, 180);

        return () => { cancelado = true; clearTimeout(timer); };
    }, [termo, aberto]);

    const abrir = useCallback((item) => {
        onFechar();
        navigate(item.rota);
    }, [navigate, onFechar]);

    const aoTeclar = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setAtivo((i) => Math.min(i + 1, resultados.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setAtivo((i) => Math.max(i - 1, 0)); }
        else if (e.key === "Enter" && resultados[ativo]) { e.preventDefault(); abrir(resultados[ativo]); }
        else if (e.key === "Escape") { onFechar(); }
    };

    if (!aberto) return null;

    const TOM = { cliente: "neutral", contrato: "accent", processo: "warn", secao: "neutral" };
    const ROTULO = { cliente: "Cliente", contrato: "Contrato", processo: "Processo", secao: "Seção" };

    return (
        <div className="busca-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
            <div className="busca-caixa elev-lg" role="dialog" aria-label="Busca rápida">
                <div className="busca-campo">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
                    </svg>
                    <input
                        ref={inputRef}
                        value={termo}
                        onChange={(e) => setTermo(e.target.value)}
                        onKeyDown={aoTeclar}
                        placeholder="Buscar cliente, contrato, processo ou seção…"
                        aria-label="Termo de busca"
                    />
                    <kbd className="busca-esc">Esc</kbd>
                </div>

                <div className="busca-resultados">
                    {resultados.map((item, i) => (
                        <button
                            key={`${item.tipo}-${item.rota}-${i}`}
                            className={`busca-item ${i === ativo ? "ativo" : ""}`}
                            onMouseEnter={() => setAtivo(i)}
                            onClick={() => abrir(item)}
                        >
                            <Tag tom={TOM[item.tipo]}>{ROTULO[item.tipo]}</Tag>
                            <span className="busca-item-corpo">
                                <span className="busca-item-titulo">{item.titulo}</span>
                                <span className="busca-item-sub">{item.sub}</span>
                            </span>
                        </button>
                    ))}
                    {termo.trim().length >= 2 && resultados.length === 0 && (
                        <p className="text-muted" style={{ padding: "16px", margin: 0 }}>
                            Nada encontrado para “{termo}”.
                        </p>
                    )}
                </div>

                <div className="busca-rodape text-muted">
                    <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
                    <span><kbd>↵</kbd> abrir</span>
                    <span><kbd>Ctrl</kbd>+<kbd>K</kbd> para abrir</span>
                </div>
            </div>
        </div>
    );
};

export default BuscaRapida;
